import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, PackageIcon, HeartIcon, LogOutIcon, MapPinIcon, CreditCardIcon, SettingsIcon, BellIcon, ShieldIcon, EditIcon } from 'lucide-react';
// This would normally come from authentication context
const mockUser = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  joined: 'March 2022',
  address: '123 Main Street, Tallinn, Estonia',
  orders: [{
    id: 'ORD-12345',
    date: '2023-10-15',
    status: 'Delivered',
    total: 299.99
  }, {
    id: 'ORD-12346',
    date: '2023-09-22',
    status: 'Processing',
    total: 149.5
  }, {
    id: 'ORD-12347',
    date: '2023-08-03',
    status: 'Delivered',
    total: 599.99
  }],
  wishlist: [{
    id: '1',
    name: 'Gold Bar 1oz',
    price: 1899.99,
    image: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80'
  }, {
    id: '2',
    name: 'MacBook Pro',
    price: 1999.99,
    image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80'
  }]
};
type TabType = 'overview' | 'orders' | 'wishlist' | 'addresses' | 'payment' | 'settings';
export const Account: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full" data-id="element-404">
      <div className="container mx-auto px-4 py-8" data-id="element-405">
        {/* Account Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6" data-id="element-406">
          <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-407">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-408">
              <div className="relative" data-id="element-409">
                <img src={mockUser.avatar} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-yellow-500" data-id="element-410" />
                <button className="absolute bottom-0 right-0 bg-yellow-500 p-1 rounded-full text-white hover:bg-yellow-600 transition-colors" data-id="element-411">
                  <EditIcon size={12} data-id="element-412" />
                </button>
              </div>
              <div className="ml-4" data-id="element-413">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-414">
                  {mockUser.name}
                </h1>
                <p className="text-gray-600 dark:text-gray-400" data-id="element-415">
                  {mockUser.email}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500" data-id="element-416">
                  Member since {mockUser.joined}
                </p>
              </div>
            </div>
            <button className="flex items-center px-4 py-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors" data-id="element-417">
              <LogOutIcon size={16} className="mr-2" data-id="element-418" />
              Sign Out
            </button>
          </div>
        </div>
        {/* Account Navigation and Content */}
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-419">
          {/* Navigation Sidebar */}
          <div className="lg:w-1/4" data-id="element-420">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden" data-id="element-421">
              <nav className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-422">
                <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'overview' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-423">
                  <UserIcon size={18} className={`mr-3 ${activeTab === 'overview' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-424" />
                  <span className={`font-medium ${activeTab === 'overview' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-425">
                    Account Overview
                  </span>
                </button>
                <button onClick={() => setActiveTab('orders')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'orders' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-426">
                  <PackageIcon size={18} className={`mr-3 ${activeTab === 'orders' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-427" />
                  <span className={`font-medium ${activeTab === 'orders' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-428">
                    Orders
                  </span>
                </button>
                <button onClick={() => setActiveTab('wishlist')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'wishlist' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-429">
                  <HeartIcon size={18} className={`mr-3 ${activeTab === 'wishlist' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-430" />
                  <span className={`font-medium ${activeTab === 'wishlist' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-431">
                    Wishlist
                  </span>
                </button>
                <button onClick={() => setActiveTab('addresses')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'addresses' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-432">
                  <MapPinIcon size={18} className={`mr-3 ${activeTab === 'addresses' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-433" />
                  <span className={`font-medium ${activeTab === 'addresses' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-434">
                    Addresses
                  </span>
                </button>
                <button onClick={() => setActiveTab('payment')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'payment' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-435">
                  <CreditCardIcon size={18} className={`mr-3 ${activeTab === 'payment' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-436" />
                  <span className={`font-medium ${activeTab === 'payment' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-437">
                    Payment Methods
                  </span>
                </button>
                <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${activeTab === 'settings' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500' : ''}`} data-id="element-438">
                  <SettingsIcon size={18} className={`mr-3 ${activeTab === 'settings' ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-439" />
                  <span className={`font-medium ${activeTab === 'settings' ? 'text-yellow-700 dark:text-yellow-500' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-440">
                    Account Settings
                  </span>
                </button>
              </nav>
            </div>
          </div>
          {/* Content Area */}
          <div className="lg:w-3/4" data-id="element-441">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6" data-id="element-442">
              {activeTab === 'overview' && <div className="animate-fadeIn" data-id="element-443">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-444">
                    Account Overview
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-445">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4" data-id="element-446">
                      <div className="flex items-center mb-3" data-id="element-447">
                        <PackageIcon size={18} className="text-yellow-500 mr-2" data-id="element-448" />
                        <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-449">
                          Recent Orders
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-450">
                        You have {mockUser.orders.length} recent orders
                      </p>
                      <Link to="#" onClick={() => setActiveTab('orders')} className="text-sm font-medium text-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400" data-id="element-451">
                        View Orders →
                      </Link>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4" data-id="element-452">
                      <div className="flex items-center mb-3" data-id="element-453">
                        <MapPinIcon size={18} className="text-yellow-500 mr-2" data-id="element-454" />
                        <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-455">
                          Default Address
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-456">
                        {mockUser.address}
                      </p>
                      <Link to="#" onClick={() => setActiveTab('addresses')} className="text-sm font-medium text-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400" data-id="element-457">
                        Manage Addresses →
                      </Link>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6" data-id="element-458">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-4" data-id="element-459">
                      Account Details
                    </h3>
                    <div className="space-y-3" data-id="element-460">
                      <div className="flex flex-col sm:flex-row sm:justify-between pb-3 border-b border-gray-100 dark:border-gray-700" data-id="element-461">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-462">
                          Name
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white" data-id="element-463">
                          {mockUser.name}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between pb-3 border-b border-gray-100 dark:border-gray-700" data-id="element-464">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-465">
                          Email
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white" data-id="element-466">
                          {mockUser.email}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between pb-3 border-b border-gray-100 dark:border-gray-700" data-id="element-467">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-468">
                          Password
                        </span>
                        <button className="text-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400 text-sm font-medium" data-id="element-469">
                          Change Password
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-470">
                    <button className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded-md transition-colors font-medium" data-id="element-471">
                      Edit Profile
                    </button>
                    <button className="flex-1 border border-gray-300 dark:border-gray-600 hover:border-yellow-500 dark:hover:border-yellow-500 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md transition-colors font-medium" data-id="element-472">
                      Delete Account
                    </button>
                  </div>
                </div>}
              {activeTab === 'orders' && <div className="animate-fadeIn" data-id="element-473">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-474">
                    Your Orders
                  </h2>
                  {mockUser.orders.length > 0 ? <div className="overflow-x-auto" data-id="element-475">
                      <table className="min-w-full" data-id="element-476">
                        <thead data-id="element-477">
                          <tr className="bg-gray-50 dark:bg-gray-700" data-id="element-478">
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-479">
                              Order ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-480">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-481">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-482">
                              Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-483">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-484">
                          {mockUser.orders.map(order => <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-485">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-486">
                                {order.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" data-id="element-487">
                                {order.date}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-488">
                                <span className={`px-2 py-1 text-xs rounded-full ${order.status === 'Delivered' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`} data-id="element-489">
                                  {order.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" data-id="element-490">
                                ${order.total.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-491">
                                <button className="text-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400 font-medium" data-id="element-492">
                                  View Details
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div> : <div className="text-center py-8" data-id="element-493">
                      <PackageIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-494" />
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-495">
                        You haven't placed any orders yet.
                      </p>
                      <Link to="/" className="mt-4 inline-block text-yellow-500 hover:text-yellow-600 font-medium" data-id="element-496">
                        Start Shopping
                      </Link>
                    </div>}
                </div>}
              {activeTab === 'wishlist' && <div className="animate-fadeIn" data-id="element-497">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-498">
                    Your Wishlist
                  </h2>
                  {mockUser.wishlist.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-499">
                      {mockUser.wishlist.map(item => <div key={item.id} className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-500">
                          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 flex-shrink-0" data-id="element-501">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-502" />
                          </div>
                          <div className="flex flex-col justify-between p-4 flex-grow" data-id="element-503">
                            <div data-id="element-504">
                              <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-505">
                                {item.name}
                              </h3>
                              <p className="text-yellow-500 font-medium" data-id="element-506">
                                ${item.price.toFixed(2)}
                              </p>
                            </div>
                            <div className="flex space-x-2 mt-2" data-id="element-507">
                              <button className="text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded transition-colors" data-id="element-508">
                                Add to Cart
                              </button>
                              <button className="text-sm text-red-500 hover:text-red-600 px-3 py-1 rounded border border-gray-200 dark:border-gray-700 transition-colors" data-id="element-509">
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>)}
                    </div> : <div className="text-center py-8" data-id="element-510">
                      <HeartIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-511" />
                      <p className="text-gray-600 dark:text-gray-400" data-id="element-512">
                        Your wishlist is empty.
                      </p>
                      <Link to="/" className="mt-4 inline-block text-yellow-500 hover:text-yellow-600 font-medium" data-id="element-513">
                        Discover Products
                      </Link>
                    </div>}
                </div>}
              {activeTab === 'addresses' && <div className="animate-fadeIn" data-id="element-514">
                  <div className="flex justify-between items-center mb-6" data-id="element-515">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-516">
                      Your Addresses
                    </h2>
                    <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-517">
                      Add New Address
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-518">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-519">
                      <div className="flex justify-between mb-2" data-id="element-520">
                        <span className="font-medium text-gray-900 dark:text-white" data-id="element-521">
                          Home
                        </span>
                        <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs px-2 py-1 rounded" data-id="element-522">
                          Default
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4" data-id="element-523">
                        John Doe
                        <br data-id="element-524" />
                        123 Main Street
                        <br data-id="element-525" />
                        Tallinn, 10111
                        <br data-id="element-526" />
                        Estonia
                      </p>
                      <div className="flex space-x-2" data-id="element-527">
                        <button className="text-sm text-yellow-500 hover:text-yellow-600" data-id="element-528">
                          Edit
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-529">
                          |
                        </span>
                        <button className="text-sm text-red-500 hover:text-red-600" data-id="element-530">
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-531">
                      <div className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-532">
                        Office
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4" data-id="element-533">
                        John Doe
                        <br data-id="element-534" />
                        456 Business Ave
                        <br data-id="element-535" />
                        Tallinn, 10112
                        <br data-id="element-536" />
                        Estonia
                      </p>
                      <div className="flex space-x-2" data-id="element-537">
                        <button className="text-sm text-yellow-500 hover:text-yellow-600" data-id="element-538">
                          Edit
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-539">
                          |
                        </span>
                        <button className="text-sm text-red-500 hover:text-red-600" data-id="element-540">
                          Remove
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-541">
                          |
                        </span>
                        <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800" data-id="element-542">
                          Set as Default
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'payment' && <div className="animate-fadeIn" data-id="element-543">
                  <div className="flex justify-between items-center mb-6" data-id="element-544">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-545">
                      Payment Methods
                    </h2>
                    <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" data-id="element-546">
                      Add Payment Method
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-547">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-548">
                      <div className="flex justify-between items-center mb-2" data-id="element-549">
                        <div className="flex items-center" data-id="element-550">
                          <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded mr-3" data-id="element-551"></div>
                          <span className="font-medium text-gray-900 dark:text-white" data-id="element-552">
                            Visa ending in 4242
                          </span>
                        </div>
                        <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs px-2 py-1 rounded" data-id="element-553">
                          Default
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-554">
                        Expires 12/2025
                      </div>
                      <div className="flex space-x-2" data-id="element-555">
                        <button className="text-sm text-yellow-500 hover:text-yellow-600" data-id="element-556">
                          Edit
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-557">
                          |
                        </span>
                        <button className="text-sm text-red-500 hover:text-red-600" data-id="element-558">
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-559">
                      <div className="flex items-center mb-2" data-id="element-560">
                        <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded mr-3" data-id="element-561"></div>
                        <span className="font-medium text-gray-900 dark:text-white" data-id="element-562">
                          Mastercard ending in 5678
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-563">
                        Expires 08/2024
                      </div>
                      <div className="flex space-x-2" data-id="element-564">
                        <button className="text-sm text-yellow-500 hover:text-yellow-600" data-id="element-565">
                          Edit
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-566">
                          |
                        </span>
                        <button className="text-sm text-red-500 hover:text-red-600" data-id="element-567">
                          Remove
                        </button>
                        <span className="text-gray-300 dark:text-gray-600" data-id="element-568">
                          |
                        </span>
                        <button className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800" data-id="element-569">
                          Set as Default
                        </button>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'settings' && <div className="animate-fadeIn" data-id="element-570">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-571">
                    Account Settings
                  </h2>
                  <div className="space-y-6" data-id="element-572">
                    <div data-id="element-573">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-574">
                        <BellIcon size={16} className="mr-2 text-yellow-500" data-id="element-575" />
                        Notifications
                      </h3>
                      <div className="space-y-2 ml-6" data-id="element-576">
                        <div className="flex items-center justify-between" data-id="element-577">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-578">
                            Email notifications
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer" data-id="element-579">
                            <input type="checkbox" className="sr-only peer" defaultChecked data-id="element-580" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500" data-id="element-581"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-582">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-583">
                            Order updates
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer" data-id="element-584">
                            <input type="checkbox" className="sr-only peer" defaultChecked data-id="element-585" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500" data-id="element-586"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-587">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-588">
                            Promotional emails
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer" data-id="element-589">
                            <input type="checkbox" className="sr-only peer" data-id="element-590" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500" data-id="element-591"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6" data-id="element-592">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-593">
                        <ShieldIcon size={16} className="mr-2 text-yellow-500" data-id="element-594" />
                        Privacy & Security
                      </h3>
                      <div className="space-y-2 ml-6" data-id="element-595">
                        <div className="flex items-center justify-between" data-id="element-596">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-597">
                            Two-factor authentication
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer" data-id="element-598">
                            <input type="checkbox" className="sr-only peer" data-id="element-599" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500" data-id="element-600"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between" data-id="element-601">
                          <span className="text-gray-600 dark:text-gray-400" data-id="element-602">
                            Activity log
                          </span>
                          <button className="text-sm text-yellow-500 hover:text-yellow-600" data-id="element-603">
                            View Log
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6" data-id="element-604">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-4" data-id="element-605">
                        Danger Zone
                      </h3>
                      <button className="w-full border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-500 dark:hover:bg-red-900/20 py-2 px-4 rounded-md transition-colors" data-id="element-606">
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};